/** "Revert change set…" — the revert's plan, the operator's decisions, and the revert.
 *
 * The plan is the backend's dry run for the CURRENT decisions — the records to skip, force over
 * conflicts, the soft-reference confirmation — so each change of a decision asks for a new plan
 * (`GET …/revert_plan/`, keyed on the decisions). The dialog never decides what blocks: it shows
 * the plan's `blockers`, and Revert stays disabled until there are none, a reason is typed and
 * change sets are enabled. The POST carries the digest of the plan on screen.
 *
 * The revert runs as a background task: the POST answers 202 with its `task_id`, and the dialog
 * stays open, locked, polling the outcome (`GET …/revert/{task_id}/`) until it is no longer
 * `pending` — the run is one transaction, and closing would hide what really happened. Then:
 * `reverted` closes with a link to the new revert change set; `stale_plan` / `revert_blocked`
 * (nothing written) re-read the plan for the operator to decide again; `revert_refused` /
 * `change_sets_disabled` / `revert_failed` (rolled back) say why. A poll that keeps failing
 * offers Retry and the background task — it never claims an outcome it has not read.
 *
 * A content or vocabulary change set needs the console permission of each kind it changes too
 * (client MCP WPS P3-16): a 403 `permission_required` shows the backend's words, and a plan with
 * conflicts the viewer may not force says what forcing needs instead of hiding the choice.
 *
 * Mounted only while open, so every opening starts from a fresh plan with nothing decided. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import * as aiChangesApi from '@/api/aiChanges';
import {
  aiChangeKeys,
  FORCE_KIND_PERMISSION_TEXT,
  needsKindPermissions,
  normalizeSkips,
  permissionRequiredOf,
  PLAN_SUMMARY_WORDS,
  REVERT_ACTION_LABELS,
  revertErrorOf,
  revertIssue,
  shortId,
  type AIChangeDetail,
  type AIChangeRevertEntry,
  type AIChangeRevertHistoryRow,
  type AIChangeRevertIssue,
  type AIChangeRevertPlan,
} from '@/api/aiChanges';
import { ConsoleApiError } from '@/api/client';
import { AppDialog } from '@/components/AppDialog';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { modelName, RecordRef } from './AIChangeEntries';
import { aiChangeHref } from './aiChangeLinks';

const OP_WORD: Record<AIChangeRevertEntry['op'], string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

export const STALE_PLAN_TEXT =
  'The records changed since this plan was made — review the updated plan and revert again.';
export const BLOCKED_TEXT = 'Some records still need a decision — see what is left below.';

/** How often the outcome is asked for while the revert runs (and the delay between retries of
 *  a failed poll). */
export const OUTCOME_POLL_MS = 1500;
/** Failed polls retried before the dialog says it cannot read the outcome. */
const OUTCOME_RETRIES = 3;

const isClientError = (e: unknown) =>
  e instanceof ConsoleApiError && e.status >= 400 && e.status < 500;

const taskHref = (taskId: string) => `/bgtasks/${encodeURIComponent(taskId)}`;

interface Notice {
  severity: 'warning' | 'error';
  text: string;
  /** `revert_failed`: the failed revert, recorded as a change set of its own. */
  failedId?: string | null;
}

const hasIssue = (plan: AIChangeRevertPlan | undefined, code: string) =>
  plan?.entries.some((e) => e.issues.some((i) => i.code === code)) ?? false;

/** "3 restored · 1 skipped" — the plan's summary, zero counts left out. */
function summaryText(summary: Record<string, number>): string {
  return Object.entries(summary)
    .filter(([, n]) => n > 0)
    .map(([key, n]) => `${n} ${PLAN_SUMMARY_WORDS[key] ?? key}`)
    .join(' · ');
}

export function RevertChangeSetDialog({
  change,
  onClose,
  pollMs = OUTCOME_POLL_MS,
}: {
  change: AIChangeDetail;
  onClose: () => void;
  /** Tests shorten it. */
  pollMs?: number;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const navigate = useNavigate();

  // The decisions drive the plan query — each combination is its own plan and digest — so they
  // are component state, not a form; the reason is the dialog's one typed input.
  const [skips, setSkips] = useState<number[]>([]);
  const [force, setForce] = useState(false);
  const [confirmSoftRefs, setConfirmSoftRefs] = useState(false);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<Notice | null>(null);
  /** The background task of the revert that was started and whose outcome is not handled yet. */
  const [taskId, setTaskId] = useState<string | null>(null);
  // A second click lands before the disabled re-render does (lessons.md, 2026-10-05).
  const submitting = useRef(false);
  /** The task whose outcome was handled — each run's outcome is acted on once. */
  const handled = useRef<string | null>(null);

  const decisions = useMemo(
    () => ({ skips, force, confirmSoftRefs }),
    [skips, force, confirmSoftRefs],
  );
  const planQuery = useQuery({
    queryKey: aiChangeKeys.revertPlan(change.id, decisions),
    queryFn: () => aiChangesApi.revertPlan(change.id, decisions),
    // Keep the last plan on screen while the next one is computed; Revert waits for it.
    placeholderData: keepPreviousData,
    // A plan describes the records as they are now: never serve a remembered one.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const plan = planQuery.data;
  const planCurrent = Boolean(plan) && !planQuery.isPlaceholderData && !planQuery.isFetching;

  const outcomeQuery = useQuery({
    queryKey: aiChangeKeys.revertOutcome(change.id, taskId ?? ''),
    queryFn: () => aiChangesApi.revertOutcome(change.id, taskId!),
    enabled: taskId !== null,
    // Poll while the task runs; stop on an outcome, and once polling has failed for good.
    refetchInterval: (q) =>
      q.state.status === 'error' || (q.state.data && q.state.data.status !== 'pending')
        ? false
        : pollMs,
    // "Keep polling a few times" through transient failures; a 4xx will not get better.
    retry: (count, error) => !isClientError(error) && count < OUTCOME_RETRIES,
    retryDelay: pollMs,
    staleTime: 0,
    gcTime: 0,
  });
  const outcome = taskId !== null ? outcomeQuery.data : undefined;
  const pollFailed = taskId !== null && outcomeQuery.isError;

  const mutation = useMutation({
    mutationFn: (digest: string) =>
      aiChangesApi.revert(change.id, {
        reason: reason.trim(),
        expected_digest: digest,
        skips: normalizeSkips(skips),
        force,
        confirm_soft_refs: confirmSoftRefs,
      }),
    onSuccess: (started) => {
      // A new run: its outcome is handled once, whatever an earlier run's was.
      handled.current = null;
      setTaskId(started.task_id);
    },
    onError: (error) => {
      const refusal = error instanceof ConsoleApiError ? revertErrorOf(error.body) : null;
      if (refusal) {
        // Refused before it started: the change set's own state may be what changed.
        setNotice({ severity: 'error', text: refusal.detail });
        void qc.invalidateQueries({ queryKey: aiChangeKeys.detail(change.id) });
        return;
      }
      const kindRefusal =
        error instanceof ConsoleApiError && error.status === 403
          ? permissionRequiredOf(error.body)
          : null;
      setNotice({
        severity: 'error',
        text:
          kindRefusal ??
          (error instanceof ConsoleApiError && error.status === 403
            ? 'You may not revert this change set (it needs console_ai_changes_revert).'
            : `The revert didn’t start: ${error instanceof Error ? error.message : 'unknown error'}`),
      });
    },
    onSettled: () => {
      submitting.current = false;
    },
  });

  // Act on an outcome once it is no longer pending — exactly once per started revert.
  useEffect(() => {
    if (taskId === null || !outcome || outcome.status === 'pending') return;
    if (handled.current === taskId) return;
    handled.current = taskId;
    setTaskId(null);
    switch (outcome.status) {
      case 'reverted':
        void qc.invalidateQueries({ queryKey: aiChangeKeys.all });
        snackbar.show('Reverted — the revert is a change set of its own.', 'success', {
          autoHideDuration: 15_000,
          ...(outcome.change_set_id
            ? {
                action: {
                  label: 'Open the revert',
                  onClick: () => navigate(aiChangeHref(outcome.change_set_id!)),
                },
              }
            : {}),
        });
        onClose();
        return;
      case 'stale_plan':
      case 'revert_blocked':
        // Nothing was written; the outcome carries no plan — read it again for these decisions.
        setNotice({
          severity: 'warning',
          text: outcome.status === 'stale_plan' ? STALE_PLAN_TEXT : BLOCKED_TEXT,
        });
        void qc.invalidateQueries({ queryKey: aiChangeKeys.revertPlan(change.id, decisions) });
        return;
      case 'revert_failed':
        setNotice({
          severity: 'error',
          text: `The revert failed and was rolled back — nothing changed. ${outcome.detail}`,
          failedId: outcome.change_set_id,
        });
        // The failed attempt is a record of its own: the page's lists change.
        void qc.invalidateQueries({ queryKey: aiChangeKeys.all });
        return;
      default:
        // revert_refused / change_sets_disabled
        setNotice({ severity: 'error', text: outcome.detail });
        void qc.invalidateQueries({ queryKey: aiChangeKeys.detail(change.id) });
    }
  }, [taskId, outcome, qc, snackbar, navigate, onClose, change.id, decisions]);

  const decide = (fn: () => void) => {
    setNotice(null);
    fn();
  };
  const toggleSkip = (seq: number) =>
    decide(() => setSkips((xs) => (xs.includes(seq) ? xs.filter((x) => x !== seq) : [...xs, seq])));

  /** A revert was started and its outcome is not in yet — the run is atomic, so wait it out. */
  const running =
    mutation.isPending ||
    (taskId !== null && !pollFailed && (outcome?.status ?? 'pending') === 'pending');
  /** Nothing may be decided or started while a revert may still be running. */
  const locked = running || pollFailed;

  const enabled = change.enabled && (plan?.enabled ?? true);
  const blockers = plan?.blockers ?? [];
  const reasonOk = reason.trim().length > 0;
  const blockReason = running
    ? 'Waiting for the outcome of the revert…'
    : pollFailed
      ? 'The outcome of the revert is not known yet.'
      : !plan
        ? null
        : !enabled
          ? 'Change sets are not enabled yet: the plan reads, but a revert cannot run.'
          : !planCurrent
            ? 'Updating the plan…'
            : blockers.length > 0
              ? `${blockers.length} ${blockers.length === 1 ? 'record still needs' : 'records still need'} a decision.`
              : !reasonOk
                ? 'Enter a reason to revert.'
                : null;
  const canRevert = Boolean(plan) && planCurrent && blockReason === null && !locked;

  const submit = () => {
    if (!plan || !canRevert || submitting.current) return;
    submitting.current = true;
    setNotice(null);
    mutation.mutate(plan.digest);
  };

  const showForce = change.permissions.force && (hasIssue(plan, 'conflict') || force);
  // P3-16: the plan has conflicts, the viewer may revert but not force this change set's kinds.
  const forceUnavailable =
    !change.permissions.force &&
    change.permissions.revert &&
    needsKindPermissions(change.domain) &&
    hasIssue(plan, 'conflict');
  const showSoftRefs = hasIssue(plan, 'soft_ref_warning') || confirmSoftRefs;
  const planRefusal =
    planQuery.error instanceof ConsoleApiError ? revertErrorOf(planQuery.error.body) : null;

  return (
    <AppDialog
      open
      onClose={onClose}
      size="md"
      disableBackdropClose
      closeDisabled={running}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Revert change set {shortId(change.id)}
          <HelpLink story="ai-changes" newTab />
        </Box>
      }
      actions={
        <>
          {blockReason && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 'auto', pl: 1 }}
              data-revert-block-reason
            >
              {blockReason}
            </Typography>
          )}
          <Button onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" disabled={!canRevert} onClick={submit}>
            {running ? 'Reverting…' : 'Revert'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Reverting puts the records this change set touched back as they were before it — as a new
        change set of its own, audited and revertible (revert the revert to redo).
      </Typography>

      {running && (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ mb: 1.5, '& .MuiAlert-message': { flex: 1 } }}
          data-revert-running
        >
          <Typography variant="body2" fontWeight={600}>
            Reverting in the background…
          </Typography>
          <Typography variant="body2">
            The revert runs as one transaction — all of it or nothing. This dialog stays open until
            its outcome is in.
            {taskId && (
              <>
                {' '}
                <Link
                  component={RouterLink}
                  to={taskHref(taskId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Follow the background task
                </Link>
              </>
            )}
          </Typography>
          <LinearProgress sx={{ mt: 1 }} aria-label="Reverting" />
        </Alert>
      )}

      {pollFailed && taskId && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          data-revert-poll-failed
          action={
            <Button
              color="inherit"
              size="small"
              disabled={outcomeQuery.isFetching}
              onClick={() => void outcomeQuery.refetch()}
            >
              Retry
            </Button>
          }
        >
          Couldn’t read the outcome of the revert
          {outcomeQuery.error instanceof Error ? ` (${outcomeQuery.error.message})` : ''}. It may
          still be running: nothing is reported as done or failed until its outcome is read.{' '}
          <Link
            component={RouterLink}
            to={taskHref(taskId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the background task
          </Link>
        </Alert>
      )}

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 1.5 }} data-revert-notice>
          {notice.text}
          {notice.failedId && (
            <>
              {' '}
              <Link
                component={RouterLink}
                to={aiChangeHref(notice.failedId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open the failed revert
              </Link>
            </>
          )}
        </Alert>
      )}

      {planQuery.isPending ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Planning the revert…
          </Typography>
        </Box>
      ) : planQuery.isError && !plan ? (
        <Alert
          severity="error"
          action={
            planRefusal ? undefined : (
              <Button color="inherit" size="small" onClick={() => void planQuery.refetch()}>
                Retry
              </Button>
            )
          }
        >
          {planRefusal
            ? planRefusal.detail
            : `Couldn’t plan the revert: ${planQuery.error instanceof Error ? planQuery.error.message : 'unknown error'}`}
        </Alert>
      ) : plan ? (
        <PlanView plan={plan} refreshing={!planCurrent} busy={locked} onToggleSkip={toggleSkip} />
      ) : null}

      {plan && (showSoftRefs || showForce) && (
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column' }}>
          {showSoftRefs && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={confirmSoftRefs}
                  disabled={locked}
                  onChange={(e) => decide(() => setConfirmSoftRefs(e.target.checked))}
                />
              }
              label="I have reviewed the soft-reference warnings"
            />
          )}
          {showForce && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={force}
                    disabled={locked}
                    onChange={(e) => decide(() => setForce(e.target.checked))}
                  />
                }
                label="Overwrite conflicts (force)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
                Restores over the later edits listed above — those edits are lost. Ops Admin only.
              </Typography>
            </>
          )}
        </Box>
      )}

      {plan && forceUnavailable && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 1 }}
          data-force-unavailable
        >
          {FORCE_KIND_PERMISSION_TEXT}
        </Typography>
      )}

      {plan && blockers.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-revert-blockers>
          <Typography variant="body2" fontWeight={600}>
            Still needs a decision
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {blockers.map((b) => (
              <li key={`${b.seq}:${b.code}`}>
                <Typography variant="body2">
                  #{b.seq} · {revertIssue(b.code).label}: {b.message}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {plan && (
        <TextField
          label="Reason"
          required
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={reason}
          disabled={locked}
          onChange={(e) => setReason(e.target.value)}
          helperText="Required — becomes the reason of the revert change set."
          sx={{ mt: 2 }}
        />
      )}
    </AppDialog>
  );
}

/** The plan: its summary, notes and the records the revert touches, each skippable where the plan
 *  allows. Also the AI vocabulary tab's revert (`VocabularyRevertDialog`). */
export function PlanView({
  plan,
  refreshing,
  busy,
  onToggleSkip,
}: {
  plan: AIChangeRevertPlan;
  refreshing: boolean;
  busy: boolean;
  onToggleSkip: (seq: number) => void;
}) {
  const summary = summaryText(plan.summary);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="subtitle2">The plan</Typography>
        {summary && (
          <Typography variant="body2" color="text.secondary" data-revert-summary>
            {summary}
          </Typography>
        )}
      </Box>
      {/* Reserved height: the bar appearing must not move the records under the pointer. */}
      <Box sx={{ height: 4, mb: 1 }}>
        {refreshing && <LinearProgress aria-label="Updating the plan" />}
      </Box>

      {plan.notes.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {plan.notes.map((n) => (
            <Typography key={n} variant="body2">
              {n}
            </Typography>
          ))}
        </Alert>
      )}
      {plan.ignored_skips.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Nothing to skip in {plan.ignored_skips.map((s) => `#${s}`).join(', ')}.
        </Typography>
      )}

      <Box role="list" aria-label="Records the revert touches">
        {plan.entries.map((entry) => (
          <PlanEntry key={entry.seq} entry={entry} disabled={busy} onToggleSkip={onToggleSkip} />
        ))}
      </Box>
    </Box>
  );
}

function PlanEntry({
  entry,
  disabled,
  onToggleSkip,
}: {
  entry: AIChangeRevertEntry;
  disabled: boolean;
  onToggleSkip: (seq: number) => void;
}) {
  const skipped = entry.skipped !== '';
  const cascading = skipped && entry.skipped !== 'requested';
  const showSkip = entry.skippable || skipped;
  const name = entry.display || `${modelName(entry.model)} #${entry.pk}`;
  const status = skipped ? null : entry.status === 'ok' ? null : revertIssue(entry.status);

  return (
    <Box
      role="listitem"
      data-plan-entry={entry.seq}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ width: 38, flex: 'none' }}>
        {/* A cascading skip is ticked but locked: it follows the record it depends on (the
            caption below names it). */}
        {showSkip && (
          <Checkbox
            size="small"
            checked={skipped}
            disabled={disabled || cascading}
            onChange={() => onToggleSkip(entry.seq)}
            inputProps={{ 'aria-label': `Skip #${entry.seq} ${name}` }}
          />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, pt: 0.75 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1,
            rowGap: 0.5,
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            #{entry.seq}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
          >
            <RecordRef kind={entry.model} pk={entry.pk} display={entry.display} newTab />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {modelName(entry.model)}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={`${OP_WORD[entry.op]} → ${REVERT_ACTION_LABELS[entry.action]}`}
          />
          {skipped ? (
            <Chip size="small" label="Skipped" />
          ) : status ? (
            <Chip
              size="small"
              color={status.kind === 'info' ? 'default' : 'warning'}
              icon={status.kind === 'info' ? <InfoOutlinedIcon /> : <WarningAmberIcon />}
              label={status.label}
            />
          ) : (
            <Chip size="small" color="success" variant="outlined" label="OK" />
          )}
        </Box>
        {cascading && (
          <Typography variant="caption" color="text.secondary" component="div">
            Skipped {entry.skipped} — it depends on that record. Untick that one to include it
            again.
          </Typography>
        )}
        {entry.skipped === 'requested' && (
          <Typography variant="caption" color="text.secondary" component="div">
            Left as it is now.
          </Typography>
        )}
        {entry.note && (
          <Typography variant="caption" color="text.secondary" component="div">
            {entry.note}
          </Typography>
        )}
        {entry.issues.map((issue, i) => (
          // Issues have no id; their order is the plan's.
          // eslint-disable-next-line react/no-array-index-key
          <IssueLine key={`${issue.code}:${i}`} issue={issue} />
        ))}
      </Box>
    </Box>
  );
}

function IssueLine({ issue }: { issue: AIChangeRevertIssue }) {
  const { label, kind } = revertIssue(issue.code);
  const where = issue.field ? `${issue.field}${issue.pointer ?? ''}` : '';
  return (
    <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }} data-revert-issue={issue.code}>
      {kind === 'info' ? (
        <InfoOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
      ) : (
        <WarningAmberIcon fontSize="small" color="warning" sx={{ mt: 0.25 }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          <strong>{label}</strong>
          {where && (
            <>
              {' · '}
              <Box component="code" sx={{ fontSize: 12 }}>
                {where}
              </Box>
            </>
          )}
          {' — '}
          {issue.message}
        </Typography>
        {issue.history?.map((h) => (
          <ConflictEdit key={`${h.history}:${h.id}`} edit={h} />
        ))}
      </Box>
    </Box>
  );
}

/** Who changed the record after the apply, and when — the edit a forced revert overwrites. */
function ConflictEdit({ edit }: { edit: AIChangeRevertHistoryRow }) {
  return (
    <Typography variant="caption" color="text.secondary" component="div" data-conflict-edit>
      Changed by {edit.actor_email || 'system'}
      {edit.created_ts && (
        <>
          {' · '}
          <Tooltip title={formatDate(edit.created_ts)} describeChild>
            <span>{formatRelativeTime(edit.created_ts)}</span>
          </Tooltip>
        </>
      )}
      {edit.source && ` · via ${edit.source}`}
      {edit.changed_paths.length > 0 && ` · ${edit.changed_paths.join(', ')}`}
      {edit.change_set && (
        <>
          {' · '}
          <Link
            component={RouterLink}
            to={aiChangeHref(edit.change_set.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            AI change {shortId(edit.change_set.id)}
          </Link>
        </>
      )}
    </Typography>
  );
}
